import { ICreationGroup, IEditionGroup } from '@/interfaces/api/group'
import type {
  IPerformanceExcludedAlarmTypeConfig,
  IPerformanceExcludedAlarmTypesResponse,
} from '@/interfaces/entities/daily-monitoring'
import ApiService from '../api.service'
import getCurrentCompanyId from '../company.service'

function performanceExcludedAlarmTypesPath(groupId: number): string {
  return `/v1/companies/${getCurrentCompanyId()}/groups/${groupId}/performance_excluded_alarm_types`
}

function normalizeExcludedAlarmTypesResponse(response: unknown): IPerformanceExcludedAlarmTypeConfig[] {
  if (!response || typeof response !== 'object') return []

  const payload = response as Partial<IPerformanceExcludedAlarmTypesResponse>
  return payload.performance_excluded_alarm_type_configs ?? []
}

const GroupResource = {
  getGroups: () => ApiService.get(`/v1/companies/${getCurrentCompanyId()}/groups`),

  createGroups: (params: ICreationGroup) => ApiService.post(`/v1/companies/${getCurrentCompanyId()}/groups`, params),

  removeGroups: (params: number) => ApiService.delete(`/v1/companies/${getCurrentCompanyId()}/groups/${params}`),

  editGroups: (params: IEditionGroup) => ApiService.put(`/v1/companies/${getCurrentCompanyId()}/groups/${params.id}`, params),

  getGroupTotalizer: ({ groupId, dateRange, shiftId }: { groupId: number; dateRange: string; shiftId: number | null }) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/groups/${groupId}/totalizer?${dateRange}&shift_id=${shiftId}`),

  getReport: ({ groupId, dateRange, shiftId }: { groupId: number; dateRange: string; shiftId: number | null }) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/groups/${groupId}/report?${dateRange}&shift_id=${shiftId}`),

  getGroupRate: ({ groupId, dateRange }: { groupId: number; dateRange: string }) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/groups/${groupId}/rate?${dateRange}`),

  getGroupOpenAlarms: (groupId: number) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/groups/${groupId}/open_alarms`),

  getGroupPerformance: (
    groupId: number,
    { start_date, end_date, shift_id }: { start_date: string; end_date: string; shift_id?: number | null },
  ) => {
    const query: Record<string, string | number> = { start_date, end_date }
    if (shift_id != null) {
      query.shift_id = shift_id
    }
    return ApiService.get(`/v1/companies/${getCurrentCompanyId()}/groups/${groupId}/performance`, query)
  },

  getPerformanceExcludedAlarmTypes: async (groupId: number): Promise<IPerformanceExcludedAlarmTypeConfig[]> => {
    const response = await ApiService.get(performanceExcludedAlarmTypesPath(groupId))
    return normalizeExcludedAlarmTypesResponse(response)
  },

  addPerformanceExcludedAlarmTypes: async (
    groupId: number,
    alarmTypeConfigIds: number[],
  ): Promise<IPerformanceExcludedAlarmTypeConfig[]> => {
    const response = await ApiService.post(performanceExcludedAlarmTypesPath(groupId), {
      alarm_type_config_ids: alarmTypeConfigIds,
    })
    return normalizeExcludedAlarmTypesResponse(response)
  },

  removePerformanceExcludedAlarmTypes: async (
    groupId: number,
    alarmTypeConfigIds: number[],
  ): Promise<IPerformanceExcludedAlarmTypeConfig[]> => {
    const response = await ApiService.deleteWithBody(performanceExcludedAlarmTypesPath(groupId), {
      alarm_type_config_ids: alarmTypeConfigIds,
    })
    return normalizeExcludedAlarmTypesResponse(response)
  },
}

export default GroupResource
